import { useCallback } from 'react';
import { Variants } from 'framer-motion';

// Staggered container animation
export const staggerContainer = (staggerChildren = 0.1, delayChildren = 0): Variants => ({
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren,
      delayChildren,
    },
  },
  exit: {
    opacity: 0,
    transition: {
      staggerChildren: staggerChildren * 0.5,
      staggerDirection: -1,
    },
  },
});

// Fade up animation item
export const fadeUpItem: Variants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      ease: [0.25, 0.46, 0.45, 0.94],
    },
  },
  exit: {
    opacity: 0,
    y: -10,
    transition: {
      duration: 0.3,
    },
  },
};

// Fade in animation item
export const fadeInItem: Variants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      duration: 0.4,
      ease: 'easeOut',
    },
  },
  exit: {
    opacity: 0,
    transition: {
      duration: 0.2,
    },
  },
};

// Scale in animation item
export const scaleInItem: Variants = {
  hidden: { opacity: 0, scale: 0.95 },
  visible: {
    opacity: 1,
    scale: 1,
    transition: {
      duration: 0.4,
      ease: [0.25, 0.46, 0.45, 0.94],
    },
  },
  exit: {
    opacity: 0,
    scale: 0.95,
    transition: {
      duration: 0.3,
    },
  },
};

// Slide in from left
export const slideInLeft: Variants = {
  hidden: { opacity: 0, x: -30 },
  visible: {
    opacity: 1,
    x: 0,
    transition: {
      duration: 0.5,
      ease: [0.25, 0.46, 0.45, 0.94],
    },
  },
  exit: {
    opacity: 0,
    x: -20,
    transition: {
      duration: 0.3,
    },
  },
};

// Slide in from right
export const slideInRight: Variants = {
  hidden: { opacity: 0, x: 30 },
  visible: {
    opacity: 1,
    x: 0,
    transition: {
      duration: 0.5,
      ease: [0.25, 0.46, 0.45, 0.94],
    },
  },
  exit: {
    opacity: 0,
    x: 20,
    transition: {
      duration: 0.3,
    },
  },
};

// Card hover animation
export const cardHover = {
  rest: {
    scale: 1,
    boxShadow: '0 4px 24px rgba(0, 0, 0, 0.2)',
    transition: { duration: 0.3, ease: 'easeOut' },
  },
  hover: {
    scale: 1.02,
    boxShadow: '0 8px 40px rgba(0, 0, 0, 0.35)',
    y: -4,
    transition: { duration: 0.3, ease: 'easeOut' },
  },
  tap: {
    scale: 0.98,
    transition: { duration: 0.1 },
  },
};

// Glow pulse animation
export const glowPulse: Variants = {
  rest: {
    boxShadow: '0 0 5px rgba(230, 0, 0, 0.2), 0 0 10px rgba(230, 0, 0, 0.05)',
  },
  animate: {
    boxShadow: [
      '0 0 5px rgba(230, 0, 0, 0.2), 0 0 10px rgba(230, 0, 0, 0.05)',
      '0 0 20px rgba(230, 0, 0, 0.4), 0 0 40px rgba(230, 0, 0, 0.15)',
      '0 0 5px rgba(230, 0, 0, 0.2), 0 0 10px rgba(230, 0, 0, 0.05)',
    ],
    transition: {
      duration: 2,
      repeat: Infinity,
      ease: 'easeInOut',
    },
  },
};

// Status dot pulse
export const statusDotPulse: Variants = {
  rest: { scale: 1 },
  animate: {
    scale: [1, 1.3, 1],
    opacity: [1, 0.7, 1],
    transition: {
      duration: 1.5,
      repeat: Infinity,
      ease: 'easeInOut',
    },
  },
};

// Page transition
export const pageTransition: Variants = {
  initial: { opacity: 0, y: 20 },
  animate: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      ease: [0.25, 0.46, 0.45, 0.94],
    },
  },
  exit: {
    opacity: 0,
    y: -20,
    transition: {
      duration: 0.3,
    },
  },
};

// Layout animation config
export const layoutAnimation = {
  type: 'spring' as const,
  stiffness: 300,
  damping: 30,
};

// Easing presets
export const easing = {
  smooth: [0.25, 0.46, 0.45, 0.94],
  bounce: [0.68, -0.55, 0.265, 1.55],
  snappy: [0.4, 0, 0.2, 1],
};

// Custom hook for generating stagger delays
export function useStaggerDelay(index: number, baseDelay = 0.1): number {
  return useCallback(() => index * baseDelay, [index, baseDelay])();
}

// Generate random sparkle animation
export function generateSparkleAnimation(): Variants {
  return {
    hidden: {
      opacity: 0,
      scale: 0,
      rotate: 0,
    },
    visible: {
      opacity: [0, 1, 1, 0],
      scale: [0, 1, 1, 0],
      rotate: [0, 180, 360],
      transition: {
        duration: 1.5 + Math.random(),
        repeat: Infinity,
        delay: Math.random() * 2,
      },
    },
  };
}

// Ripple animation for buttons
export const rippleAnimation = {
  initial: { scale: 0, opacity: 0.5 },
  animate: {
    scale: 4,
    opacity: 0,
    transition: { duration: 0.6, ease: 'easeOut' },
  },
};

// Graph node animations
export const nodeAppear: Variants = {
  hidden: {
    opacity: 0,
    scale: 0,
    filter: 'blur(10px)',
  },
  visible: {
    opacity: 1,
    scale: 1,
    filter: 'blur(0px)',
    transition: {
      duration: 0.5,
      ease: easing.smooth,
    },
  },
};

export const connectionDraw: Variants = {
  hidden: {
    pathLength: 0,
    opacity: 0,
  },
  visible: {
    pathLength: 1,
    opacity: 1,
    transition: {
      pathLength: { duration: 0.8, ease: easing.smooth },
      opacity: { duration: 0.2 },
    },
  },
};

// Tooltip animation
export const tooltipAnimation: Variants = {
  hidden: {
    opacity: 0,
    y: 10,
    scale: 0.95,
  },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      duration: 0.2,
      ease: 'easeOut',
    },
  },
  exit: {
    opacity: 0,
    y: 10,
    scale: 0.95,
    transition: {
      duration: 0.15,
    },
  },
};
